/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { memo } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import WaiverStatusTag from './index'
import { resolveWaiverStatus, WaiverStatusInput } from './resolveWaiverStatus'
import { parseWaiverRecord, waiverSummaryLabel } from './resolveWaiverSummary'

interface WaiverSummaryProps extends WaiverStatusInput {
  /** Makes the pill an action; read-only without it. */
  onClick?: () => void
}

/**
 * A single "Waiver signed" style pill for screens with no column header to say
 * what it is about. When, by whom and the imported record id are all in its
 * tooltip, so the line under the player's name stays one quiet pill.
 */
const WaiverSummary = ({ onClick, ...input }: WaiverSummaryProps) => {
  const appearance = resolveWaiverStatus(input)
  if (!appearance) {
    return null
  }

  const record = parseWaiverRecord(input.externalReference)

  const tooltip = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <Text variant='paragraphXSmall' color={colors.NEUTRAL_0}>
        {appearance.tooltip}
      </Text>
      {record ? (
        <Text
          variant='paragraphXSmall'
          color={colors.NEUTRAL_50}
          style={`
            overflow-wrap: anywhere;
          `}
        >
          {record.label}: {record.id}
        </Text>
      ) : null}
    </div>
  )

  return (
    // A row, so the pill keeps its own width instead of stretching across a
    // column parent.
    <div
      css={css`
        display: flex;
      `}
    >
      <WaiverStatusTag
        {...input}
        label={capitalizeWords(waiverSummaryLabel(appearance.label))}
        tooltip={tooltip}
        onClick={onClick}
      />
    </div>
  )
}

export default memo(WaiverSummary)
